import { allocId, World } from './world'
import { Enemy, Projectile, ProjectileBehavior, RARITY_COLOR, WeaponState } from './types'
import { ENEMIES } from '../data/enemies'
import { TAU } from '../core/math'
import { combustNova, contagionCloud, conductionArc } from './reactions'

export interface DamageRoll {
  amount: number
  crit: boolean
}

/** Refractory (sim-seconds) between hit-stop triggers from frequent sources (crits + ordinary kills). Keeps the freeze
 *  PUNCTUAL — without it a dense kill stream re-arms hitStop every frame and the game runs in permanent 6% slow-mo. */
const HITSTOP_CD = 0.13

/** Roll a damage value applying the global damage mult and crit chance. `critChanceBonus` lets a weapon
 *  (e.g. the Holy Tome) bias crits without bypassing the passive/variance pipeline. */
export function rollDamage(world: World, base: number, critChanceBonus = 0): DamageRoll {
  const s = world.stats
  let amount = base * s.damage
  // Berserker: damage scales up as current HP drops (up to +70% near death).
  if (world.passive === 'berserker') {
    const missing = 1 - world.player.hp / Math.max(1, s.maxHp)
    amount *= 1 + missing * 0.7
  }
  // Glass cannon: a flat damage surge (paired with extra damage taken).
  else if (world.passive === 'glasscannon') {
    amount *= 1.4
  }
  // evolved weapons scale their damage with their level (set by fireWeapons)
  amount *= world.evoDamageMul
  // Critical Mass: recent crits stack a temporary damage bonus
  if (world.critMassStacks > 0) amount *= 1 + world.critMassStacks * 0.02
  // Charge-shrine surge: a timed +50% damage buff (0 unless a charge shrine was channeled to full)
  if (world.dmgSurgeT > 0) amount *= 1.5
  // kill-combo power payoff: a long kill chain ramps damage (Megabonk-style snowball) — +0.35%/combo, capped at +25%.
  // Combos were cosmetic before; now chaining kills feeds back into power. Balance-affecting (canary re-baselined).
  if (world.combo > 0) amount *= 1 + Math.min(0.25, world.combo * 0.0035)
  const crit = world.rng.next() < Math.min(0.95, s.critChance + critChanceBonus)
  if (crit) amount *= s.critMult
  // small variance for feel
  amount *= 0.92 + world.rng.next() * 0.16
  return { amount, crit }
}

// MP4 — frost cross-reactions (opt-in via the Frozen Heart item). Pure, deterministic helpers.
/** Bonus "shatter" damage when a FROZEN enemy is struck — a fraction of its max HP (small vs bosses). */
export function shatterDamage(maxHp: number, isBoss: boolean): number {
  return maxHp * (isBoss ? 0.03 : 0.15)
}
/** A frozen, near-dead non-boss is executed (finished off) — the cryo build's payoff. */
export function shouldExecute(hp: number, maxHp: number, isBoss: boolean): boolean {
  return !isBoss && hp > 0 && hp < maxHp * 0.08
}

export interface HitOptions {
  crit?: boolean
  knockX?: number
  knockZ?: number
  knockForce?: number
  lifesteal?: boolean
  source?: string
  silent?: boolean
  weaponHit?: boolean // true for direct weapon hits — gates the on-hit/on-crit proc layer
  bypassArmor?: boolean // AoE/explosions ignore front-armor (a legit counter to armored foes)
}

/** Apply a damage amount to an enemy, handle floaters, lifesteal, death. */
export function dealDamage(
  world: World,
  enemy: Enemy,
  amount: number,
  opts: HitOptions = {},
): void {
  if (!enemy.alive) return
  amount = Math.max(0, amount)
  const wasFrozen = enemy.freeze > 0 // capture before any reaction clears it (MP4)
  // boss Shield power: a window of near-immunity (force the player to wait it out)
  if (enemy.bossShield > 0) amount *= 0.06
  // front-armor (armored type): frontal weapon hits are heavily reduced. Counters that
  // bypass it: DoT ticks (silent), AoE/explosions (bypassArmor), and crits (half armor).
  // A hit is "frontal" when its travel direction opposes the enemy's facing (which always
  // points at the player) — so flanking shots (boomerang returns, ricochets, rear homing)
  // and repositioning all land for full damage.
  if (enemy.frontArmor > 0 && !opts.silent && !opts.bypassArmor && (opts.knockX || opts.knockZ)) {
    const fx = Math.cos(enemy.facing)
    const fz = Math.sin(enemy.facing)
    const dot = opts.knockX! * fx + opts.knockZ! * fz
    if (dot < -0.25) {
      const reduction = enemy.frontArmor * (opts.crit ? 0.5 : 1)
      amount *= 1 - reduction
      if (!opts.silent) world.burst(enemy.x, enemy.z, 0xbfd0e0, 2, 0.4) // armor-spark "clang"
    }
  }
  // Executioner: heavy bonus damage against weakened foes (under 30% HP).
  if (world.passive === 'executioner' && enemy.hp < enemy.maxHp * 0.3) {
    amount *= 1.75
  }
  // Affliction synergy: deal more to enemies already suffering a status (burn/poison/freeze/slow). Default 0 → no-op;
  // granted by the elemental catalyst items, so a DoT/CC weapon becomes a force-multiplier for the whole build.
  if (world.stats.statusDamage > 0 && (enemy.burn > 0 || enemy.poison > 0 || enemy.freeze > 0 || enemy.slow > 0)) {
    amount *= 1 + world.stats.statusDamage
  }
  enemy.hp -= amount
  enemy.flash = 0.12
  world.damageDealt += amount
  // attribute to the source weapon for the end-of-run damage-by-weapon breakdown
  if (opts.source && amount > 0) {
    world.damageByWeapon[opts.source] = (world.damageByWeapon[opts.source] || 0) + amount
  }
  if (amount > world.highestHit) world.highestHit = amount

  // MP4 — frost cross-reactions (only with Frozen Heart owned). A direct hit on a frozen enemy
  // SHATTERS the ice for bonus max-HP-scaled damage (consuming the freeze); a frozen enemy left at
  // a sliver is EXECUTED. Opt-in build identity, deterministic, gated to direct weapon hits.
  if (world.frostReactions && wasFrozen && opts.weaponHit && !opts.silent && enemy.hp > 0) {
    let shatter = shatterDamage(enemy.maxHp, enemy.isBoss)
    if (enemy.bossShield > 0) shatter *= 0.06 // respect the boss Shield near-immunity window like the base hit does
    enemy.hp -= shatter
    enemy.freeze = 0 // ice shattered
    world.damageDealt += shatter
    if (opts.source) world.damageByWeapon[opts.source] = (world.damageByWeapon[opts.source] || 0) + shatter
    world.floatText(enemy.x, enemy.z, 'SHATTER', 0x9fe8ff)
    world.burst(enemy.x, enemy.z, 0xbfe8ff, 10, 1.2)
    if (shouldExecute(enemy.hp, enemy.maxHp, enemy.isBoss)) {
      enemy.hp = 0
      world.floatText(enemy.x, enemy.z, 'EXECUTE', 0x6cf0ff)
    }
  }

  // PT1b — Conduction (opt-in via Capacitor): a weapon CRIT arcs bonus lightning to the nearest OTHER foe.
  // Crit-gated + cascade-guarded so it's a build payoff, not every-hit spam; off without the item (canary-safe).
  if (world.conductReactions && opts.crit && opts.weaponHit && !opts.silent && !world.inExplosions && enemy.hp > 0) {
    const arc = conductionArc(amount)
    const r2 = arc.radius * arc.radius
    let best: Enemy | null = null
    let bestD = r2
    for (const o of world.enemies) {
      if (!o.alive || o === enemy) continue
      const dx = o.x - enemy.x
      const dz = o.z - enemy.z
      const d2 = dx * dx + dz * dz
      if (d2 < bestD) {
        bestD = d2
        best = o
      }
    }
    if (best) {
      world.inExplosions = true // prevent the arc from re-triggering conduction/procs
      world.floatText(enemy.x, enemy.z, 'ARC', 0x9fd0ff)
      world.burst((enemy.x + best.x) / 2, (enemy.z + best.z) / 2, 0x9fd0ff, 6, 1.0)
      dealDamage(world, best, arc.damage, { silent: true, lifesteal: false, bypassArmor: true, source: 'effects' })
      world.inExplosions = false
    }
  }

  if (!opts.silent) {
    const color = opts.crit ? 0xffd23d : 0xffffff
    world.floatText(enemy.x, enemy.z, Math.round(amount).toString(), color, !!opts.crit)
    if (opts.crit) world.critsThisFrame++
  }
  // every crit SNAPS (scaled by hit size) — was gated at dmg>35 so early-run crits had zero tactile pause. Refractory
  // (HITSTOP_CD): at high crit density this would re-arm hitStop every frame → permanent 6% slow-mo (unplayable).
  if (opts.crit && world.time - world.lastHitStopAt >= HITSTOP_CD) {
    world.hitStop = Math.max(world.hitStop, Math.min(0.06, 0.02 + amount * 0.0006))
    world.lastHitStopAt = world.time
  }

  // knockback (scaled by player knockback stat, reduced for big/boss enemies)
  if (opts.knockForce && (opts.knockX || opts.knockZ)) {
    const mass = enemy.isBoss ? 0.05 : enemy.isElite ? 0.4 : 1
    const f = opts.knockForce * world.stats.knockback * mass
    enemy.vx += opts.knockX! * f
    enemy.vz += opts.knockZ! * f
    enemy.stagger = Math.max(enemy.stagger, 0.26) // longer stagger so the bonk reads
    if (!opts.silent) world.burst(enemy.x, enemy.z, 0xffffff, 3, 0.7) // impact spark
  }

  // lifesteal
  if (opts.lifesteal !== false && world.stats.lifesteal > 0 && world.player.alive) {
    const heal = amount * world.stats.lifesteal
    healPlayer(world, heal)
  }

  // on-hit / on-crit proc layer (direct weapon hits only; not AoE/thorns/cascades)
  if (opts.weaponHit && !world.inExplosions && world.procs.length > 0) {
    applyProcs(world, 'hit', enemy, amount)
    if (opts.crit) applyProcs(world, 'crit', enemy, amount)
  }

  if (enemy.hp <= 0) killEnemy(world, enemy)
}

export function healPlayer(world: World, amount: number): void {
  if (amount <= 0 || world.noHeal) return
  const p = world.player
  p.hp = Math.min(world.stats.maxHp, p.hp + amount)
}

export function killEnemy(world: World, enemy: Enemy): void {
  if (!enemy.alive) return
  enemy.alive = false
  world.kills++
  world.killsThisFrame++
  // MP6: elite-hunt stages progress toward the boss portal by slaying elites; 'slay' stages count EVERY kill
  if (enemy.isElite && world.stageGoal === 'elite-hunt') world.goalProgress++
  else if (world.stageGoal === 'slay') world.goalProgress++

  // kill-combo: chaining kills keeps the crunch escalating
  world.combo++
  world.comboTimer = 1.5
  if (world.combo > world.bestCombo) world.bestCombo = world.combo

  // hit-stop for impact feel: meaningful kills (boss/elite — rare, discrete) always get a real freeze; ordinary kills
  // get a tiny tactile pause but ONLY through the shared HITSTOP_CD refractory, so a swarm-clear can't re-arm hitStop
  // every frame (which made the whole game crawl at 6% slow-mo when many enemies died — the "everything vibrates / can't
  // move fluidly" report). world.hitStop is consumed by the render loop only — the sim never reads it (canary-neutral).
  if (enemy.isBoss) { world.hitStop = Math.max(world.hitStop, 0.18); world.lastHitStopAt = world.time }
  else if (enemy.isElite) { world.hitStop = Math.max(world.hitStop, 0.07); world.lastHitStopAt = world.time }
  else if (world.time - world.lastHitStopAt >= HITSTOP_CD) {
    const target = Math.min(0.05, 0.012 + world.combo * 0.0006) // combo-scaled crunch, but punctual (not per-frame)
    if (world.hitStop < target) world.hitStop = target
    world.lastHitStopAt = world.time
  }

  // on-kill proc layer (suppressed during AoE resolution to avoid cascades)
  if (!world.inExplosions && world.procs.length > 0) applyProcs(world, 'kill', enemy, enemy.maxHp)

  // 'shockwave' character gimmick: chance of a knockback burst on kill
  if (world.gimmick === 'shockwave' && !world.inExplosions && world.rng.next() < 0.18) {
    world.pendingExplosions.push({ x: enemy.x, z: enemy.z, radius: 3, damage: Math.max(6, enemy.maxHp * 0.2), color: 0xffcc44, weapon: 'effects' })
  }

  // PT1 — elemental cross-reactions on death (opt-in via catalyst items; gated to non-AoE deaths so they
  // can't cascade infinitely). A burning death COMBUSTS (fire nova), a poisoned death spreads CONTAGION.
  if (!world.inExplosions) {
    if (world.combustReactions && enemy.burn > 0) combustOnKill(world, enemy)
    if (world.contagionReactions && enemy.poison > 0) contagionOnKill(world, enemy)
  }

  // death "pop": the sprite launches up and shrinks away
  if (world.corpses.length < 220) {
    const life = enemy.isBoss ? 0.85 : 0.42
    world.corpses.push({
      x: enemy.x,
      z: enemy.z,
      y: 0.2,
      vy: enemy.isBoss ? 7 : 4.5,
      // bonk launch: fling the corpse along the killing blow's knockback (enemy.vx/vz hold it at death). Bosses barely budge.
      vx: enemy.vx * (enemy.isBoss ? 0.15 : 0.7),
      vz: enemy.vz * (enemy.isBoss ? 0.15 : 0.7),
      defId: enemy.defId,
      color: enemy.color,
      size: enemy.radius * 2.2 * enemy.scale,
      life,
      maxLife: life,
    })
  }

  // Vampire passive: heal a little on every kill (scales with max HP).
  if (world.passive === 'vampire') {
    healPlayer(world, Math.max(1, world.stats.maxHp * 0.01))
  }

  // drops — curse raises drop chance (more danger, more loot)
  const curseDropMul = 1 + world.curse * 0.05
  // elites are worth more XP (and so drop a higher-tier gem); bosses already drop huge gems
  world.spawnPickup('xp', enemy.x, enemy.z, enemy.isElite && !enemy.isBoss ? enemy.xp * 1.6 : enemy.xp)
  const goldChance = (enemy.isBoss ? 1 : enemy.isElite ? 0.9 : 0.35) * curseDropMul
  if (enemy.gold > 0 && world.rng.next() < goldChance) {
    world.spawnPickup('gold', enemy.x + world.rng.range(-0.4, 0.4), enemy.z, enemy.gold)
  }
  // occasional heart from normal kills, guaranteed-ish from elites/boss
  const healChance = (enemy.isBoss ? 1 : enemy.isElite ? 0.25 : 0.012) * curseDropMul
  if (world.rng.next() < healChance) {
    world.spawnPickup('heal', enemy.x, enemy.z, enemy.isBoss ? 40 : 15)
  }

  // signature consumables (the genre's "oh hell yes" drops). Screen-clear nuke is
  // rate-limited so it stays a treat; magnet vacuums every gem on the map.
  if (enemy.isBoss) {
    world.spawnPickup('magnet', enemy.x - 1.2, enemy.z, 0)
  } else {
    if (world.time - world.lastBombAt > 25 && world.rng.next() < 0.002) {
      world.lastBombAt = world.time
      world.spawnPickup('bomb', enemy.x, enemy.z, 0)
    } else if (world.rng.next() < 0.008) {
      world.spawnPickup('magnet', enemy.x, enemy.z, 0)
    }
  }

  world.burst(enemy.x, enemy.z, enemy.color, enemy.isBoss ? 60 : enemy.isElite ? 24 : 10, enemy.isBoss ? 2.4 : 1)

  if (enemy.isBoss) {
    world.bossesKilled++
    world.emit('boss-down')
    // boss drops a chest — Boss Curse pays out extra chests for the added danger
    world.spawnPickup('chest', enemy.x, enemy.z, 1)
    for (let i = 0; i < Math.floor(world.curse / 2); i++) {
      const a = world.rng.next() * Math.PI * 2
      world.spawnPickup('chest', enemy.x + Math.cos(a) * 2.2, enemy.z + Math.sin(a) * 2.2, 1)
    }
  } else if (enemy.isMiniBoss) {
    // mini-boss beat pays out a chest + a magnet for clearing the champion
    world.emit('miniboss-down')
    world.spawnPickup('chest', enemy.x, enemy.z, 1)
    world.spawnPickup('magnet', enemy.x + 1.0, enemy.z, 0)
  }

  // explode-on-death (bombers): queue an AoE blast for the systems layer to resolve
  const ddef = ENEMIES[enemy.defId]
  if (ddef?.explodeOnDeath) {
    world.pendingExplosions.push({
      x: enemy.x,
      z: enemy.z,
      radius: ddef.explodeOnDeath.radius,
      damage: ddef.explodeOnDeath.damage * (1 + world.time / 240) * world.difficulty,
      color: 0xff7a2d,
    })
    world.burst(enemy.x, enemy.z, 0xff7a2d, 30, 1.8)
  }

  // splitter behavior
  if (enemy.splitInto && (enemy.splitCount ?? 0) > 0) {
    const def = ENEMIES[enemy.splitInto]
    if (def) {
      for (let i = 0; i < enemy.splitCount!; i++) {
        const a = (i / enemy.splitCount!) * Math.PI * 2
        const child = makeEnemyFromDef(world, enemy.splitInto, enemy.x + Math.cos(a), enemy.z + Math.sin(a))
        child.scale *= 0.85
        world.spawnEnemy(child)
      }
    }
  }
}

/** PT1 — Combust (Ember Core): a burning enemy's death bursts a fire nova — AoE damage + spreads burn.
 * Wrapped in inExplosions so nested deaths don't cascade reactions/procs infinitely. */
function combustOnKill(world: World, e: Enemy): void {
  const n = combustNova(e.maxHp, e.burnDps)
  const r2 = n.radius * n.radius
  world.inExplosions = true
  world.blast(e.x, e.z, n.radius, 0xff7a2d) // CF2 footprint ring (render-only)
  for (const o of world.enemies) {
    if (!o.alive || o === e) continue
    const dx = o.x - e.x
    const dz = o.z - e.z
    if (dx * dx + dz * dz > r2) continue
    o.burn = Math.max(o.burn, n.burnTime)
    o.burnDps = Math.min(o.burnDps + n.burnDps, Math.max(n.burnDps * 10, o.maxHp * 0.4))
    o.burnSrc = 'effects'
    dealDamage(world, o, n.damage, { silent: true, lifesteal: false, bypassArmor: true, source: 'effects' })
  }
  world.inExplosions = false
  world.floatText(e.x, e.z, 'COMBUST', 0xff9a3d, true)
  world.burst(e.x, e.z, 0xff7a2d, 16, 1.4)
}

/** PT1 — Contagion (Fungal Spores): a poisoned enemy's death seeds spores that re-poison nearby foes. */
function contagionOnKill(world: World, e: Enemy): void {
  const c = contagionCloud(e.poisonDps)
  const r2 = c.radius * c.radius
  for (const o of world.enemies) {
    if (!o.alive || o === e) continue
    const dx = o.x - e.x
    const dz = o.z - e.z
    if (dx * dx + dz * dz > r2) continue
    o.poison = Math.max(o.poison, c.poisonTime)
    o.poisonDps = Math.min(o.poisonDps + c.poisonDps, Math.max(c.poisonDps * 10, o.maxHp * 0.4))
    o.bleedStacks = Math.min(20, o.bleedStacks + 1)
    o.poisonSrc = 'effects'
  }
  world.floatText(e.x, e.z, 'CONTAGION', 0x9acd32)
  world.burst(e.x, e.z, 0x9acd32, 12, 1.2)
}

/**
 * Run owned proc triggers for a hook. Effects are deferred (explosions queue,
 * projectiles spawn) so this never recurses; cascades are blocked by world.inExplosions.
 */
function applyProcs(world: World, hook: 'hit' | 'kill' | 'crit', enemy: Enemy, amount: number): void {
  // luck biases the chance-based procs, honoring the `luck` contract ("shifts rarity / proc rolls")
  // and the Lucky Clover / gambler "better procs" promise (previously luck only touched rarity weights).
  const luckProc = 1 + world.stats.luck * 0.04
  for (const pr of world.procs) {
    if (pr.hook !== hook) continue
    const lv = pr.level
    switch (pr.kind) {
      case 'volatile': {
        if (world.rng.next() < (0.06 + 0.02 * lv) * luckProc) {
          world.pendingExplosions.push({ x: enemy.x, z: enemy.z, radius: 2.2 + lv * 0.1, damage: amount * 0.6, color: 0xff7a2d, weapon: 'effects' })
        }
        break
      }
      case 'incinerate': {
        if (world.rng.next() < (0.08 + 0.03 * lv) * luckProc) {
          // additive burn (matches applyBurn / the rest of the DoT model) so repeated procs STACK DPS
          // instead of just re-maxing to a single tick; attribute it so the end-of-run recap credits it.
          const dps = Math.max(2, amount * 0.18)
          enemy.burn = Math.max(enemy.burn, 3)
          enemy.burnDps = Math.min(enemy.burnDps + dps, Math.max(dps * 10, enemy.maxHp * 0.4))
          enemy.burnSrc = 'effects'
        }
        break
      }
      case 'static': {
        const bolt = makeProjectile({ ownerWeapon: 'static', behavior: 'straight', x: enemy.x, z: enemy.z, speed: 0, damage: amount * 0.5, chain: 1 + lv, color: 0xbfe0ff, life: 0.05 })
        bolt.hitSet.add(enemy.id)
        world.spawnProjectile(bolt)
        break
      }
      case 'critmass': {
        world.critMassStacks = Math.min(15, world.critMassStacks + 1)
        world.critMassTimer = 4
        break
      }
      case 'harvest': {
        world.harvestKills++
        const need = Math.max(8, 26 - 6 * lv)
        if (world.harvestKills >= need) {
          world.harvestKills = 0
          const a = world.rng.next() * Math.PI * 2
          const speed = 10
          // MP7: the soul-nuke runs through rollDamage too — driven by the player's DAMAGE stat + crit,
          // not the old (time * difficulty) inflation that ignored build investment.
          const d = rollDamage(world, 30 + world.time * 0.5)
          world.spawnProjectile(
            makeProjectile({ ownerWeapon: 'harvest', behavior: 'homing', x: world.player.x, z: world.player.z, vx: Math.cos(a) * speed, vz: Math.sin(a) * speed, speed, damage: d.amount, crit: d.crit, life: 3.5, pierce: 4, radius: 0.7, color: 0xb060ff, homingTurn: 6, knockback: 1 }),
          )
          world.floatText(world.player.x, world.player.z, 'SOUL NUKE', 0xb060ff, true)
        }
        break
      }
      case 'reaper': {
        if (world.rng.next() < (0.08 + 0.02 * lv) * luckProc) {
          healPlayer(world, Math.max(2, world.stats.maxHp * 0.02))
          world.pendingExplosions.push({ x: enemy.x, z: enemy.z, radius: 2.5, damage: Math.max(8, amount * 0.4), color: 0x9b6cff, weapon: 'effects' })
        }
        break
      }
      case 'frostbite': {
        // Cryo Rounds (on hit): a chance to chill — adds slow + a brief freeze, feeding freeze/shatter & statusDamage builds.
        if (world.rng.next() < (0.1 + 0.03 * lv) * luckProc) {
          enemy.slow = Math.max(enemy.slow, 1.4)
          enemy.slowAmt = Math.max(enemy.slowAmt, 0.45)
          enemy.freeze = Math.max(enemy.freeze, 0.4 + 0.05 * lv)
        }
        break
      }
      case 'siphon': {
        // Soul Siphon (on kill): convert a sliver of the fallen into a regenerating shield, capped at the player's maxShield
        // (or a small floor so it works even without shield items). Rewards an aggressive defensive playstyle.
        const cap = Math.max(20, world.stats.maxShield)
        world.player.shield = Math.min(cap, world.player.shield + 2 + lv)
        break
      }
      case 'overcharge': {
        // Overcharger (on crit): a chance to discharge a chaining arc + a small burst — turns crit builds into AoE.
        if (world.rng.next() < (0.12 + 0.04 * lv) * luckProc) {
          const bolt = makeProjectile({ ownerWeapon: 'effects', behavior: 'straight', x: enemy.x, z: enemy.z, speed: 0, damage: amount * 0.6, chain: 1 + lv, color: 0xffe27a, life: 0.05 })
          bolt.hitSet.add(enemy.id)
          world.spawnProjectile(bolt)
          world.pendingExplosions.push({ x: enemy.x, z: enemy.z, radius: 1.8 + lv * 0.1, damage: amount * 0.35, color: 0xffe27a, weapon: 'effects' })
        }
        break
      }
      case 'detonate': {
        // Cluster Bombs (on kill): a chance the corpse bursts into a fiery cluster blast — chain-clears packs.
        if (world.rng.next() < (0.12 + 0.03 * lv) * luckProc) {
          world.pendingExplosions.push({ x: enemy.x, z: enemy.z, radius: 2.4 + lv * 0.15, damage: Math.max(10, amount * 0.5), color: 0xff7a2d, weapon: 'effects' })
        }
        break
      }
    }
  }
}

/** Factory: build an Enemy from a data def at a position, scaled by difficulty/time. */
export function makeEnemyFromDef(world: World, defId: string, x: number, z: number): Enemy {
  const def = ENEMIES[defId]
  // HP and damage scale with run time and difficulty for longevity.
  const t = world.time
  const curseMul = 1 + world.curse * 0.12
  const hpScale = (1 + t / 145) * world.difficulty * curseMul
  const dmgScale = (1 + t / 240) * world.difficulty * (1 + world.curse * 0.06)
  const rewardMul = world.rewardMult * (1 + world.curse * 0.12)
  return {
    id: allocId(),
    defId,
    alive: true,
    x,
    z,
    vx: 0,
    vz: 0,
    facing: 0,
    radius: def.radius,
    hp: def.hp * hpScale,
    maxHp: def.hp * hpScale,
    damage: def.damage * dmgScale,
    speed: def.speed,
    ai: def.ai,
    xp: def.xp * rewardMul,
    gold: def.gold * rewardMul,
    isElite: false,
    isBoss: def.ai === 'boss',
    isMiniBoss: false,
    attackCd: 0,
    stagger: 0,
    flash: 0,
    contactCd: 0,
    slow: 0,
    slowAmt: 0,
    burn: 0,
    burnDps: 0,
    poison: 0,
    poisonDps: 0,
    freeze: 0,
    bleedStacks: 0,
    aiTimer: 0,
    summonCd: def.summon?.interval ?? 0,
    spawnedAt: t,
    frontArmor: def.frontArmor ?? 0,
    empoweredT: 0,
    aiSide: world.rng.next() < 0.5 ? 1 : -1,
    color: def.color,
    scale: def.scale ?? 1,
    splitInto: def.splitInto,
    splitCount: def.splitCount,
    patternIdx: 0,
    enraged: false,
    bossShield: 0,
    affix: '',
    shieldCd: 0,
  }
}

/** Convenience: default projectile with sane fields, callers override as needed. */
export function makeProjectile(over: Partial<Projectile> & { ownerWeapon: string }): Projectile {
  const proj: Projectile = {
    id: allocId(),
    alive: true,
    x: 0,
    z: 0,
    y: 0.8,
    vx: 0,
    vz: 0,
    vy: 0,
    radius: 0.4,
    damage: 10,
    crit: false,
    behavior: 'straight' as ProjectileBehavior,
    life: 2,
    pierce: 0,
    bounce: 0,
    speed: 12,
    color: 0xffffff,
    knockback: 1,
    angle: 0,
    orbitRadius: 2.5,
    orbitSpeed: 3,
    homingTurn: 4,
    returning: false,
    hitCooldown: 0,
    hitClear: 0,
    returnedToPlayer: false,
    hitSet: new Set<number>(),
    burn: 0,
    poison: 0,
    slow: 0,
    freeze: 0,
    chain: 0,
    chainBase: 0,
    explodeRadius: 0,
    explodeDamage: 0,
    pull: 0,
    ...over,
  }
  proj.chainBase = proj.chain // remember the spawn chain count so multi-hit weapons can re-arm each cycle
  return proj
}

const ORBITAL_PERSIST_LIFE = 1e9 // orbitals live for the whole run; reconciled each refresh, never expire

/** Spec for a persistent orbital/spinner weapon, recomputed from current stats each refresh tick. */
export interface OrbitalSpec {
  ownerWeapon: string
  behavior: ProjectileBehavior // 'orbit' | 'spinner'
  count: number
  damageBase: number
  radius: number
  color: number
  orbitRadius: number
  orbitSpeed: number
  knockback: number
  hitCooldown: number
  burn?: number
  poison?: number
  slow?: number
  freeze?: number
}

/**
 * MP3 — persistent orbital/spinner weapon. Instead of re-spawning a fresh short-lived batch every
 * cooldown (which reset the orbs' angle and made them blink), keep the SAME orbiting projectiles alive
 * for the run and just reconcile them each refresh tick: re-apply current stats (damage/area/speed) to
 * the existing orbs so they rotate CONTINUOUSLY, spawn only the orbs missing to reach `count`, and
 * retire any extras. Deterministic (rollDamage in index order, stable ids); tracked via ws.orbitalIds.
 */
export function refreshOrbitals(world: World, ws: WeaponState, spec: OrbitalSpec): void {
  const ids = ws.orbitalIds ?? (ws.orbitalIds = [])
  const live: Projectile[] = []
  for (let k = 0; k < ids.length; k++) {
    const pr = world.projectiles.find((p) => p.id === ids[k] && p.alive)
    if (pr) live.push(pr)
  }
  const basePhase = live.length > 0 ? live[0].angle : 0
  const out: number[] = []
  for (let i = 0; i < spec.count; i++) {
    let pr = live[i]
    if (!pr) {
      pr = makeProjectile({
        ownerWeapon: spec.ownerWeapon,
        behavior: spec.behavior,
        x: world.player.x,
        z: world.player.z,
        pierce: 999,
        angle: basePhase + (i / spec.count) * TAU, // slot new orbs into the existing ring (no snap)
      })
      world.spawnProjectile(pr)
    }
    // (re)apply current stats — reused orbs keep their angle so rotation stays continuous
    pr.ownerWeapon = spec.ownerWeapon // reused orbs (e.g. after Halo→Ring of Saturn) must re-attribute damage
    pr.behavior = spec.behavior
    const d = rollDamage(world, spec.damageBase)
    pr.damage = d.amount
    pr.crit = d.crit
    pr.radius = spec.radius
    pr.color = spec.color
    pr.orbitRadius = spec.orbitRadius
    pr.orbitSpeed = spec.orbitSpeed
    pr.knockback = spec.knockback
    pr.hitCooldown = spec.hitCooldown
    pr.burn = spec.burn ?? 0
    pr.poison = spec.poison ?? 0
    pr.slow = spec.slow ?? 0
    pr.freeze = spec.freeze ?? 0
    pr.life = ORBITAL_PERSIST_LIFE
    pr.alive = true
    out.push(pr.id)
  }
  for (let i = spec.count; i < live.length; i++) live[i].alive = false // retire extras (count dropped)
  ws.orbitalIds = out
}

export function pickupColor(world: World): number {
  return RARITY_COLOR.common
}
